import { useState, useEffect } from 'react';
import QRCode from 'qrcode';

const C = {
  green:"#16320f",greenLight:"#1f4516",greenBorder:"#3a7029",
  gold:"#d9c20d",bg:"#0c1e09",surface:"#111f0d",
  text:"#e8f0e6",textMid:"#8faa8a",textDim:"#4a6646",teal:"#34d399",
};

const STATE_ABBR = {Alabama:"AL",Alaska:"AK",Arizona:"AZ",Arkansas:"AR",California:"CA",Colorado:"CO",Connecticut:"CT",Delaware:"DE","District of Columbia":"DC",Florida:"FL",Georgia:"GA",Hawaii:"HI",Idaho:"ID",Illinois:"IL",Indiana:"IN",Iowa:"IA",Kansas:"KS",Kentucky:"KY",Louisiana:"LA",Maine:"ME",Maryland:"MD",Massachusetts:"MA",Michigan:"MI",Minnesota:"MN",Mississippi:"MS",Missouri:"MO",Montana:"MT",Nebraska:"NE",Nevada:"NV","New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND",Ohio:"OH",Oklahoma:"OK",Oregon:"OR",Pennsylvania:"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD",Tennessee:"TN",Texas:"TX",Utah:"UT",Vermont:"VT",Virginia:"VA",Washington:"WA","West Virginia":"WV",Wisconsin:"WI",Wyoming:"WY"};

function PlayerQR({ token, size }) {
  const [src, setSrc] = useState('');
  const url = token ? `${window.location.origin}/recruit/${token}` : '';
  useEffect(() => {
    if (!url) return;
    QRCode.toDataURL(url, { width: size * 6, margin: 2, errorCorrectionLevel: 'H' }).then(setSrc).catch(() => {});
  }, [url, size]);
  if (!url || !src) return null;
  return <img src={src} alt="QR" className="qr-code" style={{ display: 'block', borderRadius: 2, width: size, height: size }} />;
}

function SheetQR({ teamId, tournamentId, size }) {
  const [src, setSrc] = useState('');
  const url = `${window.location.origin}/warriors/team/${teamId}/${tournamentId}`;
  useEffect(() => {
    QRCode.toDataURL(url, { width: size * 2, margin: 1, errorCorrectionLevel: 'H', color: { dark: '#000000', light: '#ffffff' } }).then(setSrc).catch(() => {});
  }, [url, size]);
  if (!src) return null;
  return (
    <div className="sheet-qr" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <img src={src} alt="Sheet QR" style={{ display: 'block', width: size, height: size, borderRadius: 2, border: '2px solid #fff', background: '#fff' }} />
      <span className="sheet-qr-label" style={{ fontSize: 10, letterSpacing: 1, color: C.textMid, fontFamily: "'DM Mono',monospace" }}>SCAN TO VIEW DIGITAL TEAM SHEET</span>
    </div>
  );
}

function fmtDateRange(start, end) {
  if (!start) return '';
  const [sy, sm, sd] = start.split('-').map(Number);
  const s = new Date(sy, sm - 1, sd);
  const opts = { month: 'short', day: 'numeric' };
  const optsY = { month: 'short', day: 'numeric', year: 'numeric' };
  if (!end || end === start) return s.toLocaleDateString('en-US', optsY);
  const [ey, em, ed] = end.split('-').map(Number);
  const e = new Date(ey, em - 1, ed);
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', optsY)}`;
}

function RecruiterCard({ p, fields }) {
  const f = fields || {};
  const qrToken = p.shareToken && p.visibility === 'public' ? p.shareToken : '';

  return (
    <div className="recruiter-card" style={{
      border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden',
      background: '#fff', display: 'flex', flexDirection: 'row',
      height: '100%', textDecoration: 'none', color: 'inherit',
    }}>
      {/* Left panel — jersey number + grad year */}
      <div className="rc-left" style={{
        width: 36, flexShrink: 0, background: C.green,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 2px',
      }}>
        <div className="rc-num" style={{
          fontSize: 28, fontWeight: 700, color: C.gold,
          lineHeight: 1, fontFamily: "'Bebas Neue',sans-serif",
          textAlign: 'center', letterSpacing: 1,
        }}>{f.jersey !== false ? (p.jersey || '—') : ''}</div>
        {f.gradYear !== false && (
          <div style={{ fontSize: 7, color: C.textMid, fontFamily: "'DM Mono',monospace", textAlign: 'center', letterSpacing: 0.5 }}>{p.gradYear}</div>
        )}
      </div>

      {/* Photo */}
      <div className="rc-photo" style={{ width: 70, flexShrink: 0, position: 'relative', overflow: 'hidden', background: C.bg }}>
        {p.photoUrl
          ? <img src={p.photoUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block', position: 'absolute', inset: 0 }} onError={e => { e.target.style.display = 'none'; }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
              <img src="/warriors-logo.png" style={{ width: '40%', opacity: 0.18 }} alt="" />
            </div>
        }
      </div>

      {/* Info */}
      <div className="rc-info" style={{ flex: 1, padding: '5px 6px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>{p.name}</div>
          {f.positions !== false && (p.positions || []).length > 0 && (
            <div style={{ fontSize: 9, color: C.gold, fontFamily: "'DM Mono',monospace", fontWeight: 600, letterSpacing: 0.3 }}>{p.positions.join(' / ')}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(f.batThrow !== false && (p.bats || p.throws)) || (f.gpa !== false && p.gpa) ? (
            <div style={{ fontSize: 8, color: C.textMid }}>
              {f.batThrow !== false && (p.bats || p.throws) && <>B/T <strong style={{ color: C.text }}>{[p.bats, p.throws].filter(Boolean).join('/')}</strong></>}
              {f.gpa !== false && p.gpa ? <>&nbsp;·&nbsp;GPA <strong style={{ color: C.text }}>{p.gpa}</strong></> : ''}
            </div>
          ) : null}
          <div style={{ fontSize: 8, color: C.textMid, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[p.city, p.state ? (STATE_ABBR[p.state] || p.state) : ''].filter(Boolean).join(', ')}</div>
          {f.instagram !== false && p.instagram && <div style={{ fontSize: 7, color: C.teal }}>IG @{p.instagram}</div>}
          {f.twitter !== false && p.twitter && <div style={{ fontSize: 7, color: C.teal }}>𝕏 @{p.twitter}</div>}
          {f.email !== false && p.email && <div style={{ fontSize: 7, color: C.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.email}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          {p.committedSchool && f.committed !== false
            ? <div style={{ background: C.gold + '22', border: `1px solid ${C.gold}55`, borderRadius: 2, padding: '1px 4px', display: 'flex', alignItems: 'center', gap: 3 }}>
                {p.committedSchool.logoUrl && <img src={p.committedSchool.logoUrl} width={12} height={12} style={{ objectFit: 'contain', background: '#fff', borderRadius: 1, padding: 1 }} onError={e => { e.target.style.display='none'; }} />}
                <span style={{ fontSize: 6, color: C.gold, fontFamily: "'DM Mono',monospace", letterSpacing: 1 }}>COMMITTED</span>
              </div>
            : <span />
          }
          {f.qrCode !== false && qrToken && <PlayerQR token={qrToken} size={48} />}
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ p, fields, backUrl }) {
  const f = fields || {};
  const qrToken = p.shareToken && p.visibility === 'public' ? p.shareToken : '';
  const profileUrl = qrToken ? `/recruit/${p.shareToken}${backUrl ? `?from=${encodeURIComponent(backUrl)}&label=Team+Sheet` : ''}` : null;

  const cardStyle = {
    background: C.surface,
    border: `1px solid ${C.greenBorder}`,
    borderRadius: 5,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
    textDecoration: 'none',
    color: 'inherit',
    cursor: profileUrl ? 'pointer' : 'default',
    transition: 'border-color 0.15s',
  };

  const inner = (<>

      {/* Photo — aspect-ratio scales proportionally on any screen width */}
      <div className="player-photo-wrap" style={{ position: 'relative', flexShrink: 0, background: C.bg }}>
        {p.photoUrl ? (
          <img
            src={p.photoUrl}
            alt={p.name}
            className="player-photo"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%', display: 'block', position: 'absolute', inset: 0 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
            <img src="/warriors-logo.png" style={{ width: '40%', maxWidth: 56, objectFit: 'contain', opacity: 0.18 }} alt="Warriors" />
          </div>
        )}
        {/* Jersey badge — dark pill, consistent on all photo backgrounds */}
        {f.jersey !== false && p.jersey && (
          <div className="jersey-number" style={{
            position: 'absolute', top: 6, left: 7,
            background: 'rgba(0,0,0,0.68)',
            color: C.gold,
            fontFamily: "'Bebas Neue',sans-serif",
            fontSize: 18, letterSpacing: 1,
            borderRadius: 3, padding: '1px 6px',
            lineHeight: 1.3,
          }}>
            #{p.jersey}
          </div>
        )}
      </div>

      {/* Committed bar — below photo, never overlaid */}
      {p.committedSchool && f.committed !== false && (
        <div className="committed-bar" style={{
          flexShrink: 0,
          background: C.gold + '1a',
          borderTop: `1px solid ${C.gold}44`,
          borderBottom: `1px solid ${C.gold}22`,
          padding: '3px 8px',
          display: 'flex', alignItems: 'center', gap: 5,
          overflow: 'hidden',
        }}>
          <span style={{ fontSize: 7, color: C.gold, letterSpacing: 1.5, fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>COMMITTED</span>
          {p.committedSchool.logoUrl && (
            <img src={p.committedSchool.logoUrl} alt={p.committedSchool.name} width={16} height={16} style={{ objectFit: 'contain', background: '#fff', borderRadius: 2, padding: 1, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
          )}
        </div>
      )}

      {/* Info */}
      <div className="player-info-area" style={{ flex: 1, padding: '6px 8px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 2, minHeight: 0, overflow: 'hidden' }}>
        {/* Name + positions always grouped; QR floats right */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="player-name" style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2, marginBottom: 2 }}>
              {p.name}
            </div>
            {f.positions !== false && (p.positions || []).length > 0 && (
              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {p.positions.map(pos => (
                  <span key={pos} className="position-chip" style={{ fontSize: 8, padding: '1px 4px', border: `1px solid ${C.greenBorder}`, borderRadius: 2, color: C.textMid, fontFamily: "'DM Mono',monospace" }}>{pos}</span>
                ))}
              </div>
            )}
          </div>
          {f.qrCode !== false && (
            <div style={{ flexShrink: 0 }}>
              {p.customQrUrl
                ? <img src={p.customQrUrl} className="qr-code" style={{ width: 64, height: 64, display: 'block', borderRadius: 2, objectFit: 'contain', background: '#fff', padding: 2 }} alt="QR" />
                : <PlayerQR token={qrToken} size={64} />
              }
            </div>
          )}
        </div>

        <div className="player-meta" style={{ display: 'flex', flexDirection: 'column', gap: 0, fontSize: 10, color: C.textDim }}>
          {/* Line 1: Class + B/T — only renders if at least one value exists */}
          {(f.gradYear !== false && p.gradYear) || (f.batThrow !== false && (p.bats || p.throws)) ? (
            <span>
              {f.gradYear !== false && p.gradYear && (
                <>Class <span style={{ color: C.textMid, fontWeight: 600 }}>{p.gradYear}</span></>
              )}
              {f.batThrow !== false && (p.bats || p.throws) && (
                <>&nbsp;&nbsp;B:{p.bats || '?'} T:{p.throws || '?'}</>
              )}
            </span>
          ) : null}
          {/* Line 2: City, State — skipped if neither exists */}
          {(p.city || p.state) && (
            <span style={{ color: C.textMid }}>
              {[p.city, p.state ? (STATE_ABBR[p.state] || p.state) : ''].filter(Boolean).join(', ')}
            </span>
          )}
          {/* Line 3: Email — skipped if no email */}
          {f.email !== false && p.email && (
            <span>{p.email}</span>
          )}
          {/* Line 4: Instagram — skipped if no instagram */}
          {f.instagram !== false && p.instagram && (
            <span style={{ color: C.teal }}>IG @{p.instagram}</span>
          )}
          {/* Line 5: X/Twitter — skipped if no twitter */}
          {f.twitter !== false && p.twitter && (
            <span style={{ color: C.teal }}>𝕏 @{p.twitter}</span>
          )}
        </div>
      </div>
    </>);

  if (profileUrl) {
    return (
      <a href={profileUrl} className="player-card" style={cardStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = C.teal; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = C.greenBorder; }}>
        {inner}
      </a>
    );
  }
  return <div className="player-card" style={cardStyle}>{inner}</div>;
}

export default function TeamSheet({ teamId, tournamentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState('classic');
  const backToPortal = new URLSearchParams(window.location.search).get('back') === 'portal';

  useEffect(() => {
    fetch(`/api/team-sheet?teamId=${encodeURIComponent(teamId)}&tournamentId=${encodeURIComponent(tournamentId)}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || 'Not found')))
      .then(d => { setData(d); setLoading(false); if (d.team?.name && d.tournament?.name) document.title = `${d.team.name} — ${d.tournament.name} | Warrior Academy`; })
      .catch(e => { setError(typeof e === 'string' ? e : 'Sheet not available'); setLoading(false); });
  }, [teamId, tournamentId]);

  useEffect(() => {
    document.body.style.backgroundColor = '#ffffff';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontFamily: 'Inter,sans-serif', fontSize: 13 }}>Loading team sheet…</div>
  );
  if (error) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontFamily: 'Inter,sans-serif', fontSize: 14 }}>{error}</div>
  );

  const { tournament, team, teamSheet, rosterPlayers, branding } = data;
  const f = teamSheet.fields || {};
  const dateStr = fmtDateRange(tournament?.startDate, tournament?.endDate);
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const printTeamSheet = () => {
    // Row count is set directly via a CSS custom property (not a CSS transform/scale
    // hack) so the grid sizing is pure CSS — transforms applied right before printing
    // are unreliable across print engines (notably mobile/WebKit), which was causing
    // bio info to be clipped out of cards on iPhone prints.
    const container = document.querySelector('.team-sheet-container');
    const cleanup = [];

    if (layout === 'recruiting') {
      const recGrid = document.querySelector('.recruiter-grid');
      const overCapacity = rosterPlayers.length > 16;
      if (overCapacity) {
        // More than one page's worth (4x4) — let the grid flow naturally across
        // pages instead of shrinking rows to force everything onto page 1.
        document.documentElement.classList.add('print-multipage');
        document.body.classList.add('print-multipage');
        container?.classList.add('print-multipage');
        cleanup.push(() => {
          document.documentElement.classList.remove('print-multipage');
          document.body.classList.remove('print-multipage');
          container?.classList.remove('print-multipage');
        });
      } else if (recGrid) {
        const rows = Math.max(1, Math.ceil(rosterPlayers.length / 4));
        recGrid.style.setProperty('--print-rows', rows);
        cleanup.push(() => recGrid.style.removeProperty('--print-rows'));
      }
    } else {
      const cols = rosterPlayers.length > 18 ? 7 : 6;
      const rows = Math.max(1, Math.ceil(rosterPlayers.length / cols));
      const printGrid = document.querySelector('.player-grid');
      if (printGrid) {
        printGrid.style.setProperty('--print-rows', rows);
        printGrid.style.setProperty('--print-cols', cols);
      }
    }

    window.addEventListener('afterprint', () => {
      if (printGrid) {
        printGrid.style.removeProperty('--print-rows');
        printGrid.style.removeProperty('--print-cols');
      }
    }, { once: true });
    window.print();
  };

  return (
    <div className="team-sheet-container" style={{ minHeight: '100vh', background: '#ffffff', color: C.text, fontFamily: 'Inter,-apple-system,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }

        /* Photo wrapper uses padding-top trick for proportional aspect ratio */
        .player-photo-wrap {
          aspect-ratio: 3 / 4;
        }

        /* White background for page and grid so empty cells don't show app theme */
        html, body, #root { background: #ffffff !important; background-color: #ffffff !important; }

        /* Desktop: 6 columns */
        .player-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 10px;
          padding: 14px 16px;
          background: #ffffff;
        }

        /* Tablet: 3 columns */
        @media screen and (max-width: 900px) {
          .player-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; padding: 10px 12px; }
        }

        /* Mobile: 2 columns */
        @media screen and (max-width: 520px) {
          .player-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; padding: 8px; }
        }

        /* Recruiter grid — screen */
        .recruiter-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          padding: 14px 16px;
          background: #ffffff;
        }
        @media screen and (max-width: 900px) {
          .recruiter-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; padding: 10px 12px; }
        }
        @media screen and (max-width: 520px) {
          .recruiter-grid { grid-template-columns: 1fr; gap: 6px; padding: 8px; }
        }

        /* Recruiter grid — print. Row count comes from --print-rows (set in JS from
           the actual player count) so up to 16 players still fit one page; beyond
           that, .print-multipage kicks in below and lets the grid flow to page 2+
           instead of cramming everyone onto a single page. */
        @media print {
          .recruiter-grid {
            display: grid !important;
            grid-template-columns: repeat(4, 1fr) !important;
            grid-template-rows: repeat(var(--print-rows, 4), 1fr) !important;
            gap: 3px !important;
            padding: 0 !important;
            width: 100% !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            overflow: visible !important;
            background: #ffffff !important;
          }
          .recruiter-card {
            border: 1px solid #bbb !important;
            border-radius: 2px !important;
            background: #fff !important;
            height: 100% !important;
            box-sizing: border-box !important;
            overflow: visible !important;
          }

          /* More than 16 players — paginate instead of shrinking to fit one page */
          .print-multipage,
          .print-multipage .team-sheet-container {
            height: auto !important;
            overflow: visible !important;
            page-break-after: auto !important;
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          .print-multipage .recruiter-grid {
            grid-template-rows: none !important;
            grid-auto-rows: 1.95in !important;
            flex: none !important;
            overflow: visible !important;
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          .print-multipage .recruiter-card {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .rc-left {
            width: 28px !important;
            background: #16320f !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .rc-num {
            font-size: 22px !important;
            color: #d9c20d !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .rc-photo {
            width: 58px !important;
          }
          .rc-photo img {
            object-position: center top !important;
          }
          .rc-info {
            padding: 3px 5px !important;
          }
          .rc-info * { color: #000 !important; }
          .rc-info strong { color: #000 !important; font-weight: 700 !important; }
        }

        @media print {
          @page { size: 11in 8.5in landscape; margin: 0.15in; }

          html, body { width: 11in; height: 8.5in; overflow: hidden; }
          html, body, #root { background: #ffffff !important; background-color: #ffffff !important; }
          body { color: #000 !important; }
          .player-grid { background: #ffffff !important; }
          .no-print { display: none !important; }

          .team-sheet-container {
            width: 100%;
            height: calc(8.5in - 0.3in) !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden;
            page-break-after: avoid;
            page-break-inside: avoid;
          }

          * { page-break-inside: avoid !important; break-inside: avoid !important; }

          /* Force exact color rendering — no browser ink-saving adjustments */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .sheet-header {
            background: #16320f !important;
            border-bottom: 2px solid #d9c20d !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            flex-shrink: 0 !important;
            padding: 3px 14px !important;
          }
          .sheet-header-logo { width: 40px !important; height: 40px !important; }
          .sheet-header-wordmark { color: #d9c20d !important; max-height: 50px !important; max-width: 260px !important; font-size: 28px !important; }
          .sheet-header-sub { font-size: 16px !important; color: #e8f0e6 !important; }
          .recruiting-contact { flex-shrink: 0 !important; }
          .print-footer { flex-shrink: 0 !important; }

          /* Always 6 columns in print — rows use fr units so cards fill whatever
             height remains after the header/contact bar/footer, instead of a fixed
             height that can overflow the page and clip the bottom row */
          .player-grid {
            grid-template-columns: repeat(var(--print-cols, 6), 1fr) !important;
            grid-template-rows: repeat(var(--print-rows, 3), 1fr) !important;
            gap: 3px !important;
            padding: 0 !important;
            width: 100% !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            max-height: calc(8.5in - 0.3in - 1.1in) !important;
            overflow: hidden !important;
          }

          .player-card {
            border: 1px solid #bbb !important;
            background: #fff !important;
            border-radius: 2px !important;
            height: 100% !important;
            min-height: 0 !important;
            /* visible, not hidden — some mobile/WebKit print engines compute a shorter
               row height than Chrome does for the same CSS, and overflow:hidden was
               silently cropping off the bio line (class, social) when that happened */
            overflow: visible !important;
          }

          /* Photo: 50% of card height + extra, so less of the photo is
             cropped and more of the top of each player's head shows */
          .player-photo-wrap {
            aspect-ratio: unset !important;
            height: 55% !important;
            flex-shrink: 0 !important;
          }

          .player-photo { filter: grayscale(0.2); object-position: center top !important; }

          .jersey-number { font-size: 13px !important; background: #ffffff !important; color: #1a3a12 !important; padding: 1px 5px !important; border: 1px solid #1a3a12 !important; text-shadow: none !important; -webkit-text-stroke: 0 !important; opacity: 1 !important; }
          .committed-bar { padding: 1px 6px !important; font-size: 7px !important; }
          .player-info-area { padding: 2px 6px !important; gap: 0 !important; overflow: hidden !important; justify-content: flex-start !important; }

          .player-info-area > div:first-child {
            margin-bottom: 3px !important;
          }

          .player-info-area {
            position: relative !important;
          }

          .player-info-area > div:first-child {
            margin-bottom: 3px !important;
            padding-right: 52px !important;
          }

          .player-info-area > div:first-child > div:last-child {
            position: absolute !important;
            top: 2px !important;
            right: 4px !important;
          }
          .player-name { font-size: 10px !important; line-height: 1.1 !important; margin-top: -2px !important; }
          .position-chip { font-size: 7px !important; padding: 0 3px !important; }
          .player-meta {
            display: flex !important;
            flex-direction: column !important;
            gap: 0 !important;
            font-size: 7.5px !important;
            line-height: 1.4 !important;
            overflow: hidden !important;
            flex-shrink: 0 !important;
          }

          .player-meta > span {
            display: block !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .qr-code { width: 64px !important; height: 64px !important; background: #ffffff !important; border: 2px solid #ffffff !important; }
          .recruiter-card .qr-code {
            width: 72px !important;
            height: 72px !important;
            background: #ffffff !important;
            padding: 4px !important;
            border: 1px solid #cccccc !important;
            border-radius: 2px !important;
            box-sizing: border-box !important;
          }

          .print-footer { display: none !important; }
          a.player-card { text-decoration: none !important; color: inherit !important; }
          .recruiting-contact { border-bottom: 1px solid #ccc !important; padding: 2px 0 !important; }
          .recruiting-contact span, .recruiting-contact a { color: #000 !important; font-size: 9px !important; }
          .sheet-qr img { width: 56px !important; height: 56px !important; border: 1.5px solid #000 !important; background: #fff !important; }
          .sheet-qr-label { color: #333 !important; opacity: 1 !important; font-size: 6px !important; max-width: 100px !important; text-align: center !important; }

          /* Force all text to pure black for maximum print readability */
          .player-card * { color: #000000 !important; }

          /* Player name — bold black */
          .player-name { color: #000000 !important; font-weight: 700 !important; }

          /* Jersey number — dark green on white pill for print readability */
          .jersey-number { color: #1a3a12 !important; background: #ffffff !important; border: 1px solid #1a3a12 !important; text-shadow: none !important; -webkit-text-stroke: 0 !important; opacity: 1 !important; }

          /* Position chips — dark border, black text */
          .position-chip { color: #000000 !important; border-color: #333333 !important; background: transparent !important; }

          /* Muted/secondary text — dark gray instead of light green */
          .player-meta, .player-secondary { color: #333333 !important; }

          /* Card borders visible on white background */
          .player-card { border: 1px solid #cccccc !important; background: #ffffff !important; }

          /* Committed school bar */
          .committed-bar { background: #f5f0d0 !important; color: #000000 !important; }

          /* Footer */
          .team-sheet-footer { color: #333333 !important; }
        }

        .print-footer { display: none; }
      `}</style>

      {/* Header — condensed to a single row: logo/wordmark locked top-left,
          team/tournament text starts at the 25% mark, button+QR on the right */}
      <div className="sheet-header" style={{ background: C.green, borderBottom: `2px solid ${C.gold}`, padding: '8px 16px', display: 'grid', gridTemplateColumns: '25% 1fr auto', alignItems: 'center', columnGap: 12 }}>
        {/* Branding block — locked to the top-left corner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifySelf: 'start', alignSelf: 'start' }}>
          {!branding?.hideWLogo && (
            <img src="/warriors-logo.png" className="sheet-header-logo" style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }} alt="Warriors" onError={e => { e.target.style.display = 'none'; }} />
          )}
          <div>
            {branding?.wordmarkUrl
              ? <img src={branding.wordmarkUrl} alt="Warrior Academy" className="sheet-header-wordmark" style={{ maxHeight: branding?.hideWLogo ? 72 : 48, maxWidth: branding?.hideWLogo ? 340 : 240, objectFit: 'contain', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
              : <div className="sheet-header-wordmark" style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: branding?.hideWLogo ? 40 : 28, letterSpacing: 3, color: C.gold, lineHeight: 1 }}>WARRIOR ACADEMY</div>
            }
          </div>
        </div>
        {/* Team/tournament/date — starts at the 25% column mark */}
        <div className="sheet-header-sub" style={{ fontSize: 26, color: C.textMid, letterSpacing: 1, textAlign: 'left', justifySelf: 'start' }}>
          {[team?.name, team?.ageGroup, tournament?.name, dateStr].filter(Boolean).join(' · ')}
        </div>
        {/* Button + team QR — right edge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="no-print" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {backToPortal && (
              <button onClick={() => { window.location.href = '/'; }} style={{
                background: 'transparent', color: C.textMid, border: `1px solid ${C.greenBorder}`,
                borderRadius: 4, padding: '5px 12px', fontSize: 10, cursor: 'pointer',
                letterSpacing: 1, fontFamily: "'DM Mono',monospace", marginBottom: 3,
              }}>← Back to Portal</button>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${C.greenBorder}` }}>
                {['classic', 'recruiting'].map(v => (
                  <button key={v} onClick={() => setLayout(v)} style={{
                    flex: 1, padding: '5px 10px', fontSize: 9, fontWeight: 700,
                    fontFamily: "'DM Mono',monospace", letterSpacing: 1, cursor: 'pointer',
                    border: 'none', textTransform: 'uppercase',
                    background: layout === v ? C.gold : 'transparent',
                    color: layout === v ? C.bg : C.textMid,
                    transition: 'background 0.15s',
                  }}>
                    {v === 'classic' ? 'Classic' : 'Recruiting'}
                  </button>
                ))}
              </div>
              <button onClick={printTeamSheet} style={{
                background: C.gold, color: C.bg, border: 'none', borderRadius: 4,
                padding: '8px 18px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                letterSpacing: 1, fontFamily: "'DM Mono',monospace",
                boxShadow: '0 2px 8px rgba(217,194,13,0.35)',
              }}>
                🖨️ PRINT TEAM SHEET
              </button>
              <span style={{ fontSize: 8, color: C.textDim, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5, textAlign: 'center' }}>
                fits 1 page · landscape · keep scale at 100%
              </span>
            </div>
          </div>
          <SheetQR teamId={teamId} tournamentId={tournamentId} size={64} />
        </div>
      </div>

      {/* Stats bar — screen only */}
      <div className="no-print" style={{ background: C.surface, borderBottom: `1px solid ${C.greenBorder}`, padding: '6px 16px', fontSize: 10, color: C.textDim, letterSpacing: 1, display: 'flex', gap: 16 }}>
        <span>{rosterPlayers.length} PLAYERS</span>
        {team?.ageGroup && <span>{team.ageGroup}</span>}
      </div>

      {/* Recruiting Contact */}
      <div className="recruiting-contact" style={{ padding: '8px 16px', borderBottom: `1px solid ${C.greenBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, color: C.textDim, letterSpacing: 2, fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>RECRUITING CONTACT</span>
          <div style={{ flex: 1, height: 1, background: C.greenBorder + '55', flexShrink: 0, minWidth: 12 }} />
          <span style={{ fontSize: 11, color: C.text, fontFamily: "'DM Mono',monospace", letterSpacing: 0.5, fontWeight: 500 }}>RAY McDONALD</span>
          <span style={{ fontSize: 10, color: C.textDim }}>|</span>
          <span style={{ fontSize: 11, color: C.textMid, fontFamily: "'DM Mono',monospace" }}>P: (650) 477-0300</span>
          <span style={{ fontSize: 10, color: C.textDim }}>|</span>
          <a href="mailto:raymcdonald27@gmail.com" style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Mono',monospace", textDecoration: 'none' }}>E: RAYMCDONALD27@GMAIL.COM</a>
        </div>
      </div>

      {/* Cards — layout toggled by user */}
      <div className={layout === 'recruiting' ? 'recruiter-grid' : 'player-grid'}>
        {rosterPlayers.map(p => (
          layout === 'recruiting'
            ? <RecruiterCard key={p.id} p={p} fields={f} />
            : <PlayerCard key={p.id} p={p} fields={f} backUrl={`/warriors/team/${teamId}/${tournamentId}`} />
        ))}
      </div>

      {/* Print footer */}
      <div className="print-footer" style={{ marginTop: 6, borderTop: '1px solid #ccc', paddingTop: 4, fontSize: 8, color: '#555', textAlign: 'center' }}>
        warriors-academy-67d1d.web.app · contact@warrioracademysoftball.com · {today}
      </div>

      <div className="no-print" style={{ textAlign: 'center', padding: '12px 0 20px', fontSize: 11, color: C.textDim }}>
        Warrior Academy · Team Sheet
      </div>
    </div>
  );
}
